import React, { useState, useEffect } from "react";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { Table, Card, Button, message } from "antd";
import jsPDF from "jspdf";
import "jspdf-autotable";
import {
  JobApplicationStatusEnum,
  JobApplicationStatusLabel,
} from "Constants/Enums/JobApplicantStatusEnum";
import ReportService from "Services/AdminServices/ReportServices";
import moment from "moment";
import { useNavigate } from "react-router-dom";

function RecruitmentIntegration() {
  const [reportData, setReportData] = useState([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  // Filters
  const [selectedDepartment, setSelectedDepartment] = useState("All Departments");
  const [selectedMonth, setSelectedMonth] = useState("");
  const [selectedYear, setSelectedYear] = useState("");

  const monthNames = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 5 }, (_, i) => currentYear - i);

  //  Fetch data
  const fetchReport = async () => {
    setLoading(true);
    try {
      const res = await ReportService.getJobApplicantReport();
      setReportData(res.data || []);
    } catch (err) {
      console.error("Error fetching job applicant report:", err);
      message.error("Failed to fetch job applicant report");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReport();
  }, []);

  //  Filter data
  const filteredData = reportData.filter((item) => {
    const appliedDate = new Date(item.appliedOn);
    const matchesDepartment =
      selectedDepartment === "All Departments" || item.departmentName === selectedDepartment;
    const matchesMonth = !selectedMonth || appliedDate.getMonth() === parseInt(selectedMonth, 10);
    const matchesYear =
      !selectedYear || appliedDate.getFullYear().toString() === selectedYear.toString();

    return matchesDepartment && matchesMonth && matchesYear;
  });

  //  Dynamic department list
  const departmentOptions = [
    "All Departments",
    ...new Set(reportData.map((item) => item.departmentName)),
  ];

  //  Columns
  const columns = [
    {
      title: "Applicant Name",
      dataIndex: "firstName",
      key: "applicantName",
      render: (_, record) => `${record.firstName} ${record.lastName}`,
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
    },
    {
      title: "Job Title",
      dataIndex: "forJobApply",
      key: "forJobApply",
    },
    {
      title: "Department",
      dataIndex: "departmentName",
      key: "departmentName",
    },
    {
      title: "Recruiter",
      dataIndex: "recruiter",
      key: "recruiter",
    },
    
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => JobApplicationStatusLabel[status] || "N/A",
    },
    {
      title: "Applied On",
      dataIndex: "appliedOn",
      key: "appliedOn",
      render: (date) => moment(date).format("MMM-DD-YYYY"), 
    },
  ];

  //  Generate PDF report
 const handleDownloadPdf = () => {
  const doc = new jsPDF();
  const currentDate = moment().format("MMM-DD-YYYY");

  const departmentHeader =
    selectedDepartment === "All Departments"
      ? "Department: All Departments"
      : `Department: ${selectedDepartment}`;

  const monthHeader = selectedMonth !== "" ? `Month: ${monthNames[selectedMonth]}` : "";
  const yearHeader = selectedYear !== "" ? `Year: ${selectedYear}` : "";

  doc.setFontSize(18);
  doc.text("Job Applicant Report", 14, 20);

  // Line
  doc.setLineWidth(0.5);
  doc.setDrawColor(180, 180, 180);
  doc.line(14, 25, 200, 25);

  // Header Info
  doc.setFontSize(11);
  doc.text(departmentHeader, 14, 33);
  if (monthHeader) doc.text(monthHeader, 14, 39);
  if (yearHeader) doc.text(yearHeader, 14, 45);
  doc.text(`Report Generated: ${currentDate}`, 14, yearHeader ? 51 : 45);

  // Table
  const tableColumn = [
    "Applicant Name",
    "Email",
    "Job Title",
    "Department",
    "Recruiter",
    "Status",
    "Applied On",
  ];

  const tableRows = filteredData.map((item) => [
    `${item.firstName} ${item.lastName}`,
    item.email,
    item.forJobApply,
    item.departmentName,
    item.recruiter,
    JobApplicationStatusLabel[item.status] || "N/A", // <-- correct usage
    moment(item.appliedOn).format("MMM-DD-YYYY"),
  ]);

  doc.autoTable({
    head: [tableColumn],
    body: tableRows,
    startY: yearHeader ? 57 : 51,
    styles: { fontSize: 8 },
    headStyles: { fillColor: [192, 192, 192] }, // <-- gray header
  });

  doc.save("Job_Applicant_Report.pdf");
};

  return (
    <DashboardLayout>
      <div className="wrapper d-flex flex-column min-vh-100 bg-light">
        <DashboardNavbar />
        <div className="body flex-grow-1 px-3">
          <Card
            title="Job Applicant Report"
            className="card-professional"
            extra={
    <div style={{ display: "flex", gap: "8px" }}>
      <Button className="btn-back" onClick={() => navigate(-1)}>Back</Button>
      <Button className="btn-primary" onClick={handleDownloadPdf}>
        Download PDF
      </Button>
    </div>
  }

          >
            {/* Filters */}
            <div className="d-flex justify-content-end mb-3" style={{ gap: "10px" }}>
              <select
                className="form-select form-select-sm"
                value={selectedYear}
                onChange={(e) => setSelectedYear(e.target.value)}
              >
                <option value="">All Years</option>
                {years.map((year) => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </select>

              <select
                className="form-select form-select-sm"
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(e.target.value)}
              >
                <option value="">All Months</option>
                {monthNames.map((month, index) => (
                  <option key={index} value={index}>
                    {month}
                  </option>
                ))}
              </select>

              <select
                className="form-select form-select-sm"
                value={selectedDepartment}
                onChange={(e) => setSelectedDepartment(e.target.value)}
              >
                {departmentOptions.map((dept) => (
                  <option key={dept} value={dept}>
                    {dept}
                  </option>
                ))}
              </select>
            </div>

            {/* Header info before table */}
            <div className="mb-3" style={{ lineHeight: "1.6" }}>
              <p>
                <strong>Department:</strong>{" "}
                {selectedDepartment === "All Departments" ? "All Departments" : selectedDepartment}
              </p>
              {selectedMonth !== "" && (
                <p>
                  <strong>Month:</strong> {monthNames[selectedMonth]}
                </p>
              )}
              {selectedYear !== "" && (
                <p>
                  <strong>Year:</strong> {selectedYear}
                </p>
              )}
              <p>
  <strong>Report Generated:</strong> {moment().format("MMM-DD-YYYY")}
</p>
            </div>

            <Table
              className="custom-table"
              dataSource={filteredData}
              columns={columns}
              pagination={{ pageSize: 10 }}
              loading={loading}
              rowKey="jobApplicantId"
            />
          </Card>
        </div>
      </div>
    </DashboardLayout>
  );
}

export default RecruitmentIntegration;
